// Hook para gerenciamento de permissões
// Data: 2025-01-20

import { useAuth } from '../contexts/AuthContext';

// Constantes espelhadas do backend
export const USER_ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  MANAGER: 'manager',
  EMPLOYEE: 'funcionario',
  COPRODUCER: 'coprodutor',
  SELLER: 'seller'  // Vendedor/Afiliado Direto (vinculado ao produtor)
};

export const MODULES = {
  PRODUTOS: 'produtos',
  FINANCEIRO: 'financeiro',
  RELATORIOS: 'relatorios',
  VENDAS: 'vendas',
  AFILIADOS: 'afiliados',
  USUARIOS: 'usuarios',
  COMPRAS: 'compras'
};

export const ACTIONS = {
  VIEW: 'view',
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  MANAGE: 'manage',
  EXPORT: 'export'
};

// Mapeamento de rotas para permissões
const ROUTE_PERMISSIONS = {
  // Produtos
  '/products/products': [MODULES.PRODUTOS, ACTIONS.VIEW],
  '/products/register': [MODULES.PRODUTOS, ACTIONS.CREATE],
  '/products/edit': [MODULES.PRODUTOS, ACTIONS.EDIT],
  
  // Financeiro
  '/banks': [MODULES.FINANCEIRO, ACTIONS.VIEW],
  '/withdrawals': [MODULES.FINANCEIRO, ACTIONS.VIEW],
  '/responsibles': [MODULES.FINANCEIRO, ACTIONS.VIEW],
  
  // Relatórios
  '/indicators': [MODULES.RELATORIOS, ACTIONS.VIEW],
  '/performance-affiliates': [MODULES.RELATORIOS, ACTIONS.VIEW],
  '/churn': [MODULES.RELATORIOS, ACTIONS.VIEW],
  '/abandon': [MODULES.RELATORIOS, ACTIONS.VIEW],
  '/reversal': [MODULES.RELATORIOS, ACTIONS.VIEW],
  '/invoice-list': [MODULES.RELATORIOS, ACTIONS.VIEW],
  '/reports': [MODULES.RELATORIOS, ACTIONS.VIEW],
  
  // Vendas
  '/sales': [MODULES.VENDAS, ACTIONS.VIEW],
  '/sales-management': [MODULES.VENDAS, ACTIONS.VIEW],
  '/afterpay': [MODULES.VENDAS, ACTIONS.VIEW],
  '/commissions/dashboard': [MODULES.VENDAS, ACTIONS.VIEW],
  
  // Afiliados
  '/affiliates': [MODULES.AFILIADOS, ACTIONS.VIEW],
  '/my-affiliates': [MODULES.AFILIADOS, ACTIONS.VIEW],
  '/my-affiliate-requests': [MODULES.AFILIADOS, ACTIONS.VIEW],
  '/products/affiliates': [MODULES.AFILIADOS, ACTIONS.VIEW],
  
  // Usuários/Equipe
  '/team': [MODULES.USUARIOS, ACTIONS.VIEW]
};

export const usePermissions = () => {
  const { user } = useAuth();

  // Verificar se o usuário é owner
  const isOwner = () => {
    return user?.role === USER_ROLES.OWNER;
  };

  // Verificar se o usuário é admin
  const isAdmin = () => {
    return user?.role === USER_ROLES.ADMIN;
  };

  // Verificar se o usuário é customer
  const isCustomer = () => {
    return user?.role === 'customer';
  };

  // Verificar se o usuário é funcionário (employee)
  const isEmployee = () => {
    return user?.role === USER_ROLES.EMPLOYEE;
  };

  // Verificar se o usuário é seller (vendedor ou afiliado direto)
  const isSeller = () => {
    if (!user) return false;
    
    const isSellerByRole = user.role === USER_ROLES.SELLER;
    const isSellerByField = user.seller_exclusive_owner_id !== null && 
                            user.seller_exclusive_owner_id !== undefined && 
                            user.seller_exclusive_owner_id !== 0;
    
    return isSellerByRole || isSellerByField;
  };

  // Verificar se o usuário é owner ou employee (colaborador)
  const isOwnerOrEmployee = () => {
    return isOwner() || isEmployee();
  };

  // Verificar se o usuário tem uma permissão específica
  const hasPermission = (module, action) => {
    // Owner tem acesso total a tudo
    if (isOwner()) {
      return true;
    }

    // Se não há usuário logado, não tem permissão
    if (!user) {
      return false;
    }

    // Verificar permissões específicas do usuário
    try {
      const userPermissions = typeof user.permissions === 'string' 
        ? JSON.parse(user.permissions || '{}') 
        : user.permissions || {};
        
      return userPermissions[module]?.[action] || false;
    } catch (error) {
      console.error('Erro ao verificar permissões:', error);
      return false;
    }
  };

  // Verificar se o usuário pode acessar uma rota
  const canAccessRoute = (route) => {
    // Owner pode acessar qualquer rota
    if (isOwner()) {
      return true;
    }

    // SELLERS: Acesso específico a rotas permitidas
    if (isSeller()) {
      const sellerAllowedRoutes = [
        // Produtos
        '/products/seller',
        // Vendas
        '/sales',
        '/sales-management',
        '/afterpay',
        '/afterpay/reports',
        // Financeiro
        '/withdrawals',
        '/banks',
        '/responsibles',
        // Ajuda
        '/tickets',
        '/wiki'
      ];
      
      return sellerAllowedRoutes.includes(route);
    }

    // Verificar se a rota requer permissões específicas
    const routePermission = ROUTE_PERMISSIONS[route];
    if (!routePermission) {
      return true; // Se não há restrição definida, permite acesso
    }

    const [module, action] = routePermission;
    return hasPermission(module, action);
  };

  // Verificar múltiplas permissões de uma vez
  const hasAnyPermission = (permissionChecks) => {
    return permissionChecks.some(([module, action]) => hasPermission(module, action));
  };

  // Verificar todas as permissões
  const hasAllPermissions = (permissionChecks) => {
    return permissionChecks.every(([module, action]) => hasPermission(module, action));
  };

  // Obter todas as permissões do usuário
  const getUserPermissions = () => {
    if (isOwner()) {
      // Owner tem todas as permissões
      return {
        [MODULES.PRODUTOS]: { [ACTIONS.VIEW]: true, [ACTIONS.CREATE]: true, [ACTIONS.EDIT]: true, [ACTIONS.DELETE]: true },
        [MODULES.FINANCEIRO]: { [ACTIONS.VIEW]: true, [ACTIONS.MANAGE]: true },
        [MODULES.RELATORIOS]: { [ACTIONS.VIEW]: true, [ACTIONS.EXPORT]: true },
        [MODULES.VENDAS]: { [ACTIONS.VIEW]: true, [ACTIONS.MANAGE]: true },
        [MODULES.AFILIADOS]: { [ACTIONS.VIEW]: true, [ACTIONS.MANAGE]: true },
        [MODULES.USUARIOS]: { [ACTIONS.VIEW]: true, [ACTIONS.CREATE]: true, [ACTIONS.EDIT]: true, [ACTIONS.DELETE]: true }
      };
    }

    try {
      return typeof user?.permissions === 'string' 
        ? JSON.parse(user.permissions || '{}') 
        : user?.permissions || {};
    } catch (error) {
      console.error('Erro ao obter permissões do usuário:', error);
      return {};
    }
  };

  // Verificar se usuário pode gerenciar outros usuários
  const canManageUsers = () => {
    return isOwner() || hasPermission(MODULES.USUARIOS, ACTIONS.CREATE);
  };

  // Obter role do usuário
  const getUserRole = () => {
    return user?.role || '';
  };

  // Verificar se usuário está ativo
  const isUserActive = () => {
    return user?.status === 'active';
  };

  return {
    // Verificações básicas
    isOwner,
    isAdmin,
    isCustomer,
    isEmployee,
    isSeller,
    isOwnerOrEmployee,
    hasPermission,
    canAccessRoute,
    hasAnyPermission,
    hasAllPermissions,
    
    // Informações do usuário
    getUserPermissions,
    getUserRole,
    isUserActive,
    
    // Verificações específicas
    canManageUsers,
    
    // Constantes
    USER_ROLES,
    MODULES,
    ACTIONS
  };
};




